import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { ME, GET_MY_ADS, GET_MY_FAVORITES } from '../graphql/queries';
import { UPDATE_PROFILE, UPDATE_PROFILE_PICTURE } from '../graphql/mutations';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { getAbsoluteImageUrl } from '../lib/imageUtils';
import { CameraIcon, MapPinIcon, PhoneIcon, EnvelopeIcon, EyeIcon, HeartIcon, ChartBarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { Form, Button, Card, Alert, Tabs, Tab, Row, Col, Table, Badge } from 'react-bootstrap';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { data, refetch } = useQuery(ME);
  const [updateProfile] = useMutation(UPDATE_PROFILE);
  const [updateProfilePicture] = useMutation(UPDATE_PROFILE_PICTURE);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'saved'>('overview');

  // Infinite scroll state
  const [displayedAds, setDisplayedAds] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const ADS_PER_PAGE = 20;

  // Fetch user's ads and favorites
  const { data: adsData, error: adsError, loading: adsLoading, fetchMore } = useQuery(GET_MY_ADS, {
    variables: { status: null, limit: ADS_PER_PAGE, offset: 0 },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      console.error('Error fetching my ads:', error);
    }
  });

  const { data: favoritesData } = useQuery(GET_MY_FAVORITES, {
    fetchPolicy: 'cache-and-network',
  });


  const userData = data?.me || user;
  const allMyAds = useMemo(() => adsData?.myAds || [], [adsData?.myAds]);
  const myFavorites = favoritesData?.myFavorites || [];

  // Update displayed ads when data changes
  useEffect(() => {
    if (allMyAds.length > 0) {
      if (offset === 0) {
        // Initial load
        setDisplayedAds(allMyAds);
        setHasMore(allMyAds.length === ADS_PER_PAGE);
      } else {
        // Append new ads
        setDisplayedAds(prev => [...prev, ...allMyAds]);
        setHasMore(allMyAds.length === ADS_PER_PAGE);
      }
    }
  }, [allMyAds, offset]);

  // Debug logging
  console.log('Profile Debug:');
  console.log('User:', userData);
  console.log('Total ads fetched:', allMyAds.length);
  console.log('Displayed ads:', displayedAds.length);
  console.log('Offset:', offset);
  console.log('Has more:', hasMore);
  console.log('Ads Loading:', adsLoading);
  console.log('Ads Error:', adsError);

  const [formData, setFormData] = useState({
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    displayName: userData?.displayName || '',
    bio: userData?.bio || '',
    phone: userData?.phone || '',
    address: userData?.address || '',
    city: userData?.city || '',
    website: userData?.website || '',
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await updateProfilePicture({
            variables: { image: reader.result }
          });
          refetch();
          setMessage('Profile picture updated!');
        } catch (error) {
          setMessage('Failed to update profile picture');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await updateProfile({
        variables: { input: formData }
      });
      if (result.data?.updateProfile?.success) {
        setMessage('Profile updated successfully!');
        setIsEditing(false);
        refetch();
      } else {
        setMessage(result.data?.updateProfile?.message || 'Failed to update profile');
      }
    } catch (error) {
      setMessage('An error occurred');
    }
  };

  // Load more ads function
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || adsLoading) return;

    setIsLoadingMore(true);
    const newOffset = offset + ADS_PER_PAGE;

    try {
      await fetchMore({
        variables: {
          offset: newOffset,
          limit: ADS_PER_PAGE,
          status: null
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const newAds = fetchMoreResult.myAds || [];
          setOffset(newOffset);
          return {
            myAds: [...(prev.myAds || []), ...newAds]
          };
        }
      });
    } catch (error) {
      console.error('Error loading more ads:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [offset, hasMore, adsLoading, isLoadingMore, fetchMore]);

  // Intersection Observer for infinite scroll
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && displayedAds.length >= 30) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoadingMore, displayedAds.length, loadMore]);

  if (!userData) {
    return (
      <Layout showSidebar={false}>
        <div className="text-center py-5">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout showSidebar={false} containerSize="sm">
      <div className="pw-page">
      {/* Cover Image */}
      <div className="position-relative rounded-top-4 overflow-hidden" style={{ height: '192px', background: 'linear-gradient(to right, var(--bs-primary), var(--bs-secondary))' }}>
          {userData.coverImage && (
            <img
              src={getAbsoluteImageUrl(userData.coverImage) || ''}
              alt="Cover"
              className="w-100 h-100 object-cover rounded-top-4"
            />
          )}
        </div>

        {/* Profile Info */}
        <Card className="rounded-bottom-4 rounded-top-0 border-0 pw-card">
          <Card.Body className="px-4 pb-4">
            {/* Avatar */}
            <div className="position-relative mb-n5" style={{ marginTop: '-64px' }}>
              <div className="position-relative d-inline-block">
                <div className="bg-light rounded-circle border border-4 border-white overflow-hidden" style={{ width: '128px', height: '128px' }}>
                  {userData.profilePicture ? (
                    <img
                      src={getAbsoluteImageUrl(userData.profilePicture) || ''}
                      alt={userData.displayName}
                      className="w-100 h-100 object-cover"
                    />
                  ) : (
                    <div className="w-100 h-100 d-flex align-items-center justify-content-center fs-1 fw-bold text-muted">
                      {userData.firstName?.[0]}{userData.lastName?.[0]}
                    </div>
                  )}
                  <label className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-center cursor-pointer opacity-0 hover-opacity-100 transition-opacity bg-dark bg-opacity-50" style={{ cursor: 'pointer' }}>
                    <CameraIcon className="h-8 w-8 text-white" style={{ width: '32px', height: '32px' }} />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="d-none"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="pt-5">
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h1 className="display-6 fw-bold text-dark">{userData.displayName}</h1>
                  <p className="text-secondary">@{userData.username}</p>
                  <Badge bg={userData.userType === 'company' ? 'primary' : 'success'} className="mt-2">
                    {userData.userType === 'company' ? 'Company' : 'Individual'}
                  </Badge>
                </div>
                <Button
                  variant="outline-secondary"
                  onClick={() => setIsEditing(!isEditing)}
                >
                  {isEditing ? 'Cancel' : 'Edit Profile'}
                </Button>
              </div>

              {message && (
                <Alert variant={message.includes('success') ? 'success' : 'danger'} className="mb-4">
                  {message}
                </Alert>
              )}

              {isEditing ? (
                <Form onSubmit={handleSubmit} className="space-y-4">
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      />
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Display Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Bio</Form.Label>
                    <Form.Control
                      as="textarea"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows={4}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Website</Form.Label>
                    <Form.Control
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    />
                  </Form.Group>
                  <div className="d-flex gap-3">
                    <Button variant="primary" type="submit">
                      Save Changes
                    </Button>
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              ) : (
                <div>
                  {userData.bio && (
                    <div className="mb-4">
                      <h3 className="h5 fw-semibold text-dark mb-2">About</h3>
                      <p className="text-secondary">{userData.bio}</p>
                    </div>
                  )}

                  <Row className="g-3 mb-4">
                    <Col md={6} className="d-flex align-items-center text-secondary">
                      <EnvelopeIcon className="me-3" style={{ width: '20px', height: '20px' }} />
                      {userData.email}
                    </Col>
                    {userData.phone && (
                      <Col md={6} className="d-flex align-items-center text-secondary">
                        <PhoneIcon className="me-3" style={{ width: '20px', height: '20px' }} />
                        {userData.phone}
                      </Col>
                    )}
                    {userData.city && (
                      <Col md={6} className="d-flex align-items-center text-secondary">
                        <MapPinIcon className="me-3" style={{ width: '20px', height: '20px' }} />
                        {userData.city}
                      </Col>
                    )}
                  </Row>

                  <div className="pt-4 border-top">
                    <h3 className="h5 fw-semibold text-dark mb-3">Account Stats</h3>
                    <Row className="g-3">
                      <Col xs={4}>
                        <Card className="text-center bg-light border-0 pw-card">
                          <Card.Body className="py-3">
                            <div className="fs-3 fw-bold text-primary">{userData.maxActiveListings}</div>
                            <div className="small text-secondary">Max Listings</div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={4}>
                        <Card className="text-center bg-light border-0 pw-card">
                          <Card.Body className="py-3">
                            <div className="fs-3 fw-bold text-success">{userData.remainingListings}</div>
                            <div className="small text-secondary">Remaining</div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={4}>
                        <Card className="text-center bg-light border-0 pw-card">
                          <Card.Body className="py-3">
                            <div className="fs-3 fw-bold text-info">
                              {userData.isVerified ? 'Yes' : 'No'}
                            </div>
                            <div className="small text-secondary">Verified</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>

                  {/* Tabs Section */}
                  <div className="pt-4 border-top">
                    <Tabs
                      activeKey={activeTab}
                      onSelect={(k) => setActiveTab(k as any)}
                      className="mb-4"
                    >
                      <Tab eventKey="overview" title="Overview">
                        {/* Overview Tab Content */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h3 className="h5 fw-semibold text-dark">
                            My Recent Ads
                            {adsLoading && <span className="ms-2 small text-primary">(Loading...)</span>}
                          </h3>
                          {displayedAds.length > 0 && (
                            <span className="small text-muted">
                              Showing {displayedAds.length} {displayedAds.length >= 30 && hasMore ? '+' : ''} ads
                            </span>
                          )}
                        </div>

                        {/* Debug info */}
                        {process.env.NODE_ENV === 'development' && (
                          <Alert variant="info" className="mb-3">
                            <p className="mb-1"><strong>Debug Info:</strong></p>
                            <p className="mb-1">Total fetched: {allMyAds.length}</p>
                            <p className="mb-1">Displayed: {displayedAds.length}</p>
                            <p className="mb-1">Offset: {offset}</p>
                            <p className="mb-1">Has more: {hasMore ? 'Yes' : 'No'}</p>
                            <p className="mb-1">Loading more: {isLoadingMore ? 'Yes' : 'No'}</p>
                            <p className="mb-0">Error: {adsError ? adsError.message : 'None'}</p>
                          </Alert>
                        )}

                        {displayedAds.length === 0 && !adsLoading ? (
                          <Card className="bg-light text-center py-5">
                            <Card.Body>
                              <p className="text-secondary mb-3">You haven't posted any ads yet.</p>
                              <Link
                                to="/post-ad"
                                className="btn btn-primary"
                              >
                                Post Your First Ad
                              </Link>
                            </Card.Body>
                          </Card>
                        ) : (
                          <div>
                            <Row className="g-3">
                              {displayedAds.map((ad: any) => (
                                <Col md={6} key={ad.id}>
                                  <Link
                                    to={`/ad/${ad.slug}`}
                                    className="text-decoration-none"
                                  >
                                    <Card className="h-100 border hover-shadow transition-shadow">
                                      <Card.Body className="p-3">
                                        <div className="d-flex gap-3">
                                          {ad.imagesList && ad.imagesList.length > 0 ? (
                                            <img
                                              src={getAbsoluteImageUrl(ad.imagesList.find((img: any) => img.isPrimary)?.url || ad.imagesList[0].url) || '/placeholder-ad.png'}
                                              alt={ad.title}
                                              className="rounded object-cover"
                                              style={{ width: '96px', height: '96px' }}
                                            />
                                          ) : (
                                            <div
                                              className="bg-light rounded d-flex align-items-center justify-content-center"
                                              style={{ width: '96px', height: '96px' }}
                                            >
                                              <span className="text-muted fs-4">📷</span>
                                            </div>
                                          )}
                                          <div className="flex-1 flex-grow-1">
                                            <h4 className="fw-semibold text-dark text-truncate">{ad.title}</h4>
                                            <p className="fs-5 fw-bold text-primary mb-1">
                                              Rs. {Number(ad.price).toLocaleString()}
                                            </p>
                                            <div className="d-flex align-items-center gap-3 small text-secondary">
                                              <span className="d-flex align-items-center gap-1">
                                                <EyeIcon style={{ width: '16px', height: '16px' }} />
                                                {ad.viewCount || 0}
                                              </span>
                                              <Badge bg={
                                                ad.status === 'active' ? 'success' :
                                                ad.status === 'pending' ? 'warning' :
                                                ad.status === 'sold' ? 'primary' :
                                                ad.status === 'featured' ? 'info' :
                                                'danger'
                                              }>
                                                {ad.status}
                                              </Badge>
                                            </div>
                                          </div>
                                        </div>
                                      </Card.Body>
                                    </Card>
                                  </Link>
                                </Col>
                              ))}
                            </Row>

                            {/* Infinite scroll trigger */}
                            {hasMore && displayedAds.length >= 30 && (
                              <div
                                ref={observerTarget}
                                className="d-flex align-items-center justify-content-center py-4"
                              >
                                {isLoadingMore ? (
                                  <div className="d-flex align-items-center gap-3">
                                    <div className="spinner-border text-primary" role="status" style={{ width: '20px', height: '20px' }}>
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span className="text-secondary">Loading more ads...</span>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <ChevronDownIcon className="text-muted" style={{ width: '32px', height: '32px', margin: '0 auto', animation: 'bounce 1s infinite' }} />
                                    <p className="small text-muted mt-2">
                                      Scroll down to see more
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* End of list indicator */}
                            {!hasMore && displayedAds.length > 0 && (
                              <div className="text-center py-4">
                                <p className="text-muted small">
                                  🎉 You've reached the end! Showing all {displayedAds.length} ads.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </Tab>

                      <Tab eventKey="analytics" title="Analytics">
                        {/* Analytics Tab Content */}
                        <h3 className="h5 fw-semibold text-dark mb-3">Ad Performance</h3>
                        <Row className="g-3 mb-4">
                          <Col md={4}>
                            <Card className="border-0" style={{ background: 'linear-gradient(to bottom right, #eff6ff, #dbeafe)' }}>
                              <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <EyeIcon className="text-primary" style={{ width: '32px', height: '32px' }} />
                                  <span className="small text-primary fw-medium">Total Views</span>
                                </div>
                                <p className="fs-2 fw-bold text-dark mb-0">
                                  {displayedAds.reduce((sum: number, ad: any) => sum + (ad.viewCount || 0), 0)}
                                </p>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col md={4}>
                            <Card className="border-0" style={{ background: 'linear-gradient(to bottom right, #fef2f2, #fee2e2)' }}>
                              <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <HeartIcon className="text-danger" style={{ width: '32px', height: '32px' }} />
                                  <span className="small text-danger fw-medium">Total Favorites</span>
                                </div>
                                <p className="fs-2 fw-bold text-dark mb-0">
                                  {displayedAds.reduce((sum: number, ad: any) => sum + (ad.favoriteCount || 0), 0)}
                                </p>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col md={4}>
                            <Card className="border-0" style={{ background: 'linear-gradient(to bottom right, #f0fdf4, #dcfce7)' }}>
                              <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <ChartBarIcon className="text-success" style={{ width: '32px', height: '32px' }} />
                                  <span className="small text-success fw-medium">Active Ads</span>
                                </div>
                                <p className="fs-2 fw-bold text-dark mb-0">
                                  {displayedAds.filter((ad: any) => ad.status === 'active').length}
                                </p>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        {/* Per-Ad Analytics */}
                        <h4 className="fw-semibold text-dark mb-3">Individual Ad Performance</h4>
                        <Card className="border overflow-hidden">
                          <Table responsive hover className="mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="px-3 py-2 text-start small fw-medium text-secondary">Ad</th>
                                <th className="px-3 py-2 text-center small fw-medium text-secondary">Views</th>
                                <th className="px-3 py-2 text-center small fw-medium text-secondary">Favorites</th>
                                <th className="px-3 py-2 text-center small fw-medium text-secondary">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayedAds.map((ad: any) => (
                                <tr key={ad.id}>
                                  <td className="px-3 py-3">
                                    <Link to={`/ad/${ad.slug}`} className="fw-medium text-dark text-decoration-none text-truncate d-block" style={{ maxWidth: '200px' }}>
                                      {ad.title}
                                    </Link>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <span className="d-inline-flex align-items-center gap-1 text-secondary">
                                      <EyeIcon style={{ width: '16px', height: '16px' }} />
                                      {ad.viewCount || 0}
                                    </span>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <span className="d-inline-flex align-items-center gap-1 text-secondary">
                                      <HeartIcon style={{ width: '16px', height: '16px' }} />
                                      {ad.favoriteCount || 0}
                                    </span>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <Badge bg={
                                      ad.status === 'active' ? 'success' :
                                      ad.status === 'pending' ? 'warning' :
                                      ad.status === 'sold' ? 'primary' :
                                      'secondary'
                                    }>
                                      {ad.status}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card>
                      </Tab>

                      <Tab eventKey="saved" title={`Saved Ads (${myFavorites.length})`}>
                        {/* Saved Ads Tab Content */}
                        <h3 className="h5 fw-semibold text-dark mb-3">Saved Ads</h3>
                        {myFavorites.length === 0 ? (
                          <Card className="bg-light text-center py-5">
                            <Card.Body>
                              <HeartIcon className="text-muted mb-3" style={{ width: '64px', height: '64px', margin: '0 auto' }} />
                              <p className="text-secondary mb-3">You haven't saved any ads yet.</p>
                              <Link
                                to="/"
                                className="btn btn-primary"
                              >
                                Browse Ads
                              </Link>
                            </Card.Body>
                          </Card>
                        ) : (
                          <Row className="g-3">
                            {myFavorites.map((ad: any) => (
                              <Col md={4} key={ad.id}>
                                <Link
                                  to={`/ad/${ad.slug}`}
                                  className="text-decoration-none"
                                >
                                  <Card className="h-100 border hover-shadow transition-shadow overflow-hidden">
                                    <div className="position-relative">
                                      {ad.primaryImage ? (
                                        <img
                                          src={getAbsoluteImageUrl(ad.primaryImage) || '/placeholder-ad.png'}
                                          alt={ad.title}
                                          className="w-100 object-cover"
                                          style={{ height: '192px' }}
                                        />
                                      ) : (
                                        <div
                                          className="bg-light d-flex align-items-center justify-content-center w-100"
                                          style={{ height: '192px' }}
                                        >
                                          <span className="text-muted fs-1">📷</span>
                                        </div>
                                      )}
                                      <button
                                        className="position-absolute top-0 end-0 m-2 p-2 bg-white rounded-circle shadow border-0"
                                        style={{ marginTop: '8px', marginRight: '8px' }}
                                      >
                                        <HeartIcon className="text-danger" style={{ width: '20px', height: '20px' }} />
                                      </button>
                                    </div>
                                    <Card.Body className="p-3">
                                      <h4 className="fw-semibold text-dark text-truncate mb-2" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ad.title}</h4>
                                      <p className="fs-5 fw-bold text-primary mb-2">
                                        Rs. {Number(ad.price).toLocaleString()}
                                      </p>
                                      <div className="d-flex align-items-center small text-secondary">
                                        <MapPinIcon className="me-1" style={{ width: '16px', height: '16px' }} />
                                        {ad.city || ad.location}
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </Link>
                              </Col>
                            ))}
                          </Row>
                        )}
                      </Tab>
                    </Tabs>
                  </div>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>
    </Layout>
  );
};

export default Profile;
